import { useMemo } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { DesignPackageServices } from "@/services/bedrock/DesignPackageServices";
import { DesignPackageSummary } from "@/services/bedrock/dtos/DesignPackage";
import { Page } from "@/services/bedrock/dtos/Page";
import { designKeys } from "@/services/bedrock/queryKeys";
import { useSocialCoachSession } from "@/utils/useSocialCoachSession";

// Newest designs first; one page covers most catalogs, and the look gallery fetches the rest
export const DESIGNS_PAGE_SIZE = 24;

/** The page after `page`, or undefined once the list is complete. */
export function nextDesignsPage(page: Page<DesignPackageSummary>, pageNumber: number): number | undefined {
  const { totalPages, totalCount, perPage, hasMore } = page.pagination;
  if (hasMore != null) return hasMore ? pageNumber + 1 : undefined;
  const total = totalPages ?? (totalCount != null && perPage > 0 ? Math.ceil(totalCount / perPage) : undefined);
  return total != null && pageNumber < total ? pageNumber + 1 : undefined;
}

/**
 * Every published review design the player can use, most recently published first, loaded a page
 * at a time. Only review designs: they are the looks a post can pick between, and a first page of
 * them answers "is there a review design at all" wherever that gates a share card. A design opened
 * from the library (any type) is fetched by id by the share flow. A player's own come from the
 * session server-side; a coach names the player. A null id keeps the query off. `data` is every
 * design loaded so far; `hasMore` and `fetchNextPage` get the rest.
 */
export function useAvailableDesigns(playerId: number | null | undefined) {
  const { isPlayer } = useSocialCoachSession();
  const query = useInfiniteQuery({
    queryKey: designKeys.availableDesigns(playerId ?? -1, "review"),
    queryFn: ({ pageParam }) =>
      DesignPackageServices.list({
        scope: "available",
        ...(isPlayer ? {} : { playerId: playerId ?? undefined }),
        contextType: "review",
        sort: "publishedAt",
        order: "desc",
        page: pageParam,
        perPage: DESIGNS_PAGE_SIZE,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, _pages, lastPageParam) => nextDesignsPage(lastPage, lastPageParam),
    enabled: playerId != null,
    staleTime: 60_000,
  });

  const data = useMemo(() => (query.data ? query.data.pages.flatMap(page => page.content) : undefined), [query.data]);

  return {
    data,
    isLoading: query.isLoading,
    isFetched: query.isFetched,
    fetchStatus: query.fetchStatus,
    error: query.error,
    hasMore: query.hasNextPage,
    isLoadingMore: query.isFetchingNextPage,
    /** The last page request failed; callers stop asking until told to retry. */
    loadMoreFailed: query.isFetchNextPageError,
    fetchNextPage: query.fetchNextPage,
  };
}
